/**
 * Parent OKR Selector Component
 * Allows selecting a parent OKR for alignment based on hierarchy rules
 */

import React, { useState } from 'react';
import { useAvailableParentOKRs } from '../../hooks';
import { useAuth } from '../../context/AuthContext';
import { Search, X, ChevronDown, Link2, Info } from 'lucide-react';

interface ParentOKRSelectorProps {
  okrType: 'company' | 'department' | 'team' | 'individual';
  value: string | null;
  onChange: (parentOKRId: string | null) => void;
  excludeId?: string;
  disabled?: boolean;
}

export const ParentOKRSelector: React.FC<ParentOKRSelectorProps> = ({
  okrType,
  value,
  onChange,
  excludeId,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();

  const { data: availableParents = [], isLoading } = useAvailableParentOKRs(
    okrType,
    excludeId
  );

  const isEmployee = user?.role === 'employee';
  const showEmployeeNote = isEmployee && okrType === 'individual' && availableParents.length > 0;

  const selectedParent = availableParents.find(okr => okr.id === value);

  const filteredParents = availableParents.filter(okr =>
    okr.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${okr.owner.firstName} ${okr.owner.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (parentId: string) => {
    onChange(parentId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm('');
  };

  // Determine if this OKR type can have parents
  const canHaveParent = okrType !== 'company';

  if (!canHaveParent) {
    return (
      <div className="text-sm text-gray-500 italic">
        Company OKRs cannot have a parent OKR
      </div>
    );
  }

  if (availableParents.length === 0 && !isLoading) {
    return (
      <div className="text-sm text-gray-500 italic">
        No available parent OKRs found for {okrType} OKRs
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Parent OKR (Optional)
        <span className="ml-2 text-xs text-gray-500">
          Align this OKR to a higher-level objective
        </span>
      </label>

      {/* Employee info note */}
      {showEmployeeNote && (
        <div className="mb-3 flex items-start gap-2 text-xs text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Showing department and team OKRs from your department for alignment
          </span>
        </div>
      )}

      {/* Selected value display / Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className={`
          w-full px-4 py-2.5 text-left border rounded-lg
          flex items-center justify-between
          transition-colors duration-150
          ${disabled || isLoading
            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-white border-gray-300 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          }
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {selectedParent ? (
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">
                {selectedParent.title}
              </div>
              <div className="text-xs text-gray-500">
                <span className="capitalize">{selectedParent.type}</span>
                {' • '}
                {selectedParent.owner.firstName} {selectedParent.owner.lastName}
              </div>
            </div>
          ) : (
            <span className="text-gray-500">
              {isLoading ? 'Loading...' : 'Select a parent OKR (optional)'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 ml-2">
          {selectedParent && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  handleClear();
                }
              }}
              className="p-1 hover:bg-gray-100 rounded cursor-pointer"
            >
              <X className="w-4 h-4 text-gray-400" />
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search OKRs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto">
            {filteredParents.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No OKRs found matching "{searchTerm}"
              </div>
            ) : (
              <div className="py-1">
                {filteredParents.map((okr) => (
                  <button
                    key={okr.id}
                    type="button"
                    onClick={() => handleSelect(okr.id)}
                    className={`
                      w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors
                      border-b border-gray-100 last:border-b-0
                      ${okr.id === value ? 'bg-blue-50' : ''}
                    `}
                  >
                    <div className="font-medium text-gray-900 mb-1">
                      {okr.title}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                      <span className="px-2 py-0.5 bg-gray-100 rounded capitalize">
                        {okr.type}
                      </span>
                      <span>
                        {okr.owner.firstName} {okr.owner.lastName}
                      </span>
                      {(okr.owner as any).department && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                          {(okr.owner as any).department}
                        </span>
                      )}
                      <span className={`
                        px-2 py-0.5 rounded capitalize
                        ${okr.status === 'active' ? 'bg-green-100 text-green-700' : ''}
                        ${okr.status === 'draft' ? 'bg-gray-100 text-gray-700' : ''}
                        ${okr.status === 'on_track' ? 'bg-blue-100 text-blue-700' : ''}
                        ${okr.status === 'at_risk' ? 'bg-yellow-100 text-yellow-700' : ''}
                      `}>
                        {okr.status.replace('_', ' ')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
